import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { LabpartnerService } from '../services/labpartner.service';
import {
  DetailsGeneratedStatus,
  Experiment,
  ReadyToTestStatus,
  StudyType,
} from '../services/labpartner.service.model';
import { ExperimentService } from '../shared/experiment.service';
import { BaseComponent } from '../support/base.component';

import { MatTabChangeEvent } from '@angular/material/tabs';
import { takeUntil } from 'rxjs/operators';
import { AppStateService } from '../services/app-state.service';
import { AuditService } from '../services/audit.service';
import { AuditCountMap } from '../services/audit.service.models';
import { ConfigurationService } from '../services/configuration.service';
import { FeatureFlags } from '../services/configuration.service.model';
import { StudyTypeService } from '../services/experiment.service';
import { EVENT_OPEN_EXPERIMENT, TAB_PAGE_NAMES } from '../services/logging-constants';
import { LoggingService } from '../services/logging.service';
import { NotesService } from '../services/notes-service.service';
import { UserAccountService } from '../services/user-account.service';

@Component({
  selector: 'app-experiment-detail',
  templateUrl: './experiment-detail.component.html',
  styleUrls: ['./experiment-detail.component.scss'],
  standalone: false,
})
export class ExperimentDetailComponent extends BaseComponent implements OnInit, OnDestroy {
  auditCountMap?: AuditCountMap;

  experiment$!: Observable<Experiment>;
  selectedIndex: number = 0;
  inputSelected: boolean = false;

  currentExperiment: Experiment | null = null;
  currentRoles: string[] = [];

  StudyType = StudyType;

  currentTabName: string = '';

  private readonly _destroying$ = new Subject<void>();

  featureFlags$: Observable<FeatureFlags | null>;

  constructor(
    public service: ExperimentService,
    public accountService: UserAccountService,
    public apiService: LabpartnerService,
    public notesService: NotesService,
    private auditService: AuditService,
    private route: ActivatedRoute,
    private appState: AppStateService,
    private loggingService: LoggingService,
    private router: Router,
    private studyTypeService: StudyTypeService,
    private configService: ConfigurationService,
  ) {
    super();
    this.featureFlags$ = this.configService.featureFlags$;
  }

  protected ngOnDestroyInternal(): void {}

  ngOnInit() {
    const id = this.route.snapshot.params['id'] as number;
    const tab = this.route.snapshot.params['tab'] as number;
    this.selectedIndex = tab;
    this.experiment$ = this.apiService.getExperiment(id);

    this.appState.ExperimentOpened.pipe(takeUntil(this._destroying$)).subscribe(e => {
      this.currentExperiment = e;
    });

    this.accountService.currentRoles$.pipe(takeUntil(this._destroying$)).subscribe(roles => {
      this.currentRoles = roles;
    });

    this.subscription.add(
      this.auditService.auditCountMap.subscribe(acm => {
        this.auditCountMap = acm;
      }),
    );

    this.subscription.add(
      this.experiment$.subscribe(exp => {
        this.appState.SetCurrentExperiment(exp);
        const props: { [key: string]: number } = {
          ExperimentId: exp.experimentId,
          DeviceType: exp.deviceType,
        };
        this.loggingService.logEvent(EVENT_OPEN_EXPERIMENT, props);
        if (
          this.selectedIndex > 4 || // If the tab index is greater than 4, reset to 0
          (this.selectedIndex > 2 && exp.status < DetailsGeneratedStatus) || // If the tab index is greater than 2 and the details are not generated, reset to 0
          (this.selectedIndex == 4 &&
            (!this.shouldShowDIPTab(exp) || exp.status < ReadyToTestStatus)) // If the tab index is 4 and the experiment is not a DIP type or the experiment is not ready to test, reset to 0
        ) {
          this.router.navigate(['/experiment-detail', exp.experimentId, 0]);
          this.selectedIndex = 0;
        } else if (
          this.selectedIndex == 4 &&
          this.shouldShowDIPTab(exp) &&
          exp.status >= ReadyToTestStatus
        ) {
          // If the tab index is 4 and the experiment is a DIP type and the experiment is ready to test, grant access.
          this.currentTabName = 'Data Insights Preview';
        }
      }),
    );

    // Input is now considered 5.
    this.trackPageView(5);
  }

  selectedTabChanged(event: MatTabChangeEvent, exp: Experiment) {
    this.currentTabName = event.tab.textLabel;
    if (event.index < 0 || event.index > 4) {
      console.error('unexpected tab index');
      return;
    }

    this.router.navigate(['/experiment-detail', exp.experimentId, event.index]);
    if (event.index == 0) {
      this.inputSelected = true;
    } else {
      this.inputSelected = false;
    }

    // Accommodate tracking for tab switching, essentially maintain Logistics as 2, Uploads as 3, and Details as 4
    // Sub to Input, Sample is 0 and Conditions is 1. Input is now considered 5.
    this.trackPageView(this.inputSelected ? 5 : event.index + 1);
  }

  // This is a helper function to determine if the DIP tab should be shown.
  shouldShowDIPTab(experiment: Experiment): boolean {
    return this.studyTypeService.getStudyTypeConfig(experiment.studyType)?.enableDIPTab ?? false;
  }

  private trackPageView(pageIndex: number) {
    const pageName = TAB_PAGE_NAMES[pageIndex];
    this.loggingService.logPageView(pageName);
  }
}
